import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query'
import { type PaginatedResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import { fetchSupplier, fetchSuppliers, fetchAllSuppliers } from '../api/supplierApi'
import { type Supplier } from '../utils/types'
import { supplierKeys } from '../utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'

/**
 * Hook to fetch paginated suppliers
 */
export function useSuppliers(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Supplier>, Error> {
  return useQuery({
    queryKey: supplierKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
      expand: options.expand,
    }),
    queryFn: () => fetchSuppliers(options),
    enabled,
  })
}

/**
 * Hook to fetch a single supplier by ID or URL
 */
export function useSupplier(
  idOrUrl: string | number | undefined,
  expand?: string[],
  enabled = true
): UseQueryResult<Supplier, Error> {
  return useQuery({
    queryKey: supplierKeys.detail(typeof idOrUrl === 'number' ? idOrUrl : Number(getPKfromSelf(String(idOrUrl))), { expand }),
    queryFn: () => {
      if (!idOrUrl) {
        throw new Error('Supplier ID or URL is required')
      }
      return fetchSupplier(idOrUrl, expand)
    },
    enabled: enabled && idOrUrl !== undefined,
  })
}

/**
 * Hook to fetch all suppliers (all pages)
 */
export function useAllSuppliers(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Supplier[], Error> {
  return useQuery({
    queryKey: [...supplierKeys.all, 'all', options],
    queryFn: () => fetchAllSuppliers(options),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - suppliers don't change often
  })
}

/**
 * Hook to fetch suppliers by IDs returned from search results.
 */
export function useSuppliersFromSearch(
  supplierIds: Array<string | number>,
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: supplierIds.map(id => ({
      queryKey: supplierKeys.detail(typeof id === 'number' ? id : Number(getPKfromSelf(String(id))), { expand }),
      queryFn: () => fetchSupplier(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    })),
  })

  const suppliers = queries
    .filter(query => query.isSuccess && query.data)
    .map(query => query.data as Supplier)

  const isLoading = queries.some(query => query.isLoading)
  const isIdle = queries.every(query => query.status === 'pending')
  const isDone = queries.every(query => query.status !== 'pending')
  const hasErrors = queries.some(query => query.isError)
  const errors = queries.filter(query => query.isError).map(query => query.error)

  const totalQueries = supplierIds.length
  const completedQueries = queries.filter(query => query.status !== 'pending').length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    suppliers,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries,
  }
}

